import axios from 'axios';
import type {
  Cell,
  Pack,
  Test,
  PaginatedResponse,
  CreatePackInput,
  CreateTestInput,
  CreateCellInput,
  AuditLog,
} from './types';

export type { PaginatedResponse, CreateCellInput, CreatePackInput, CreateTestInput };

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3002/api' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
});

function utf8ToBase64UrlPath(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

export const getErrorMessage = (error: any): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.response?.data?.message || error.message || 'An unexpected error occurred';
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred';
};

export const packsApi = {
  list: async (params?: {
    status?: string;
    customer?: string;
    search?: string;
    limit?: number;
    lastKey?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
  }): Promise<PaginatedResponse<Pack>> => {
    const response = await api.get<PaginatedResponse<Pack>>('/packs', {
      params,
    });
    return response.data;
  },

  get: async (id: string): Promise<Pack> => {
    const response = await api.get<Pack>(`/packs/${id}`);
    return response.data;
  },

  create: async (data: CreatePackInput): Promise<Pack> => {
    const response = await api.post<Pack>('/packs', data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/packs/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<CreatePackInput>): Promise<Pack> => {
    const response = await api.patch<Pack>(`/packs/${id}`, data);
    return response.data;
  },
};

export const cellsApi = {
  list: async (params?: {
    batch_id?: string;
    received_box?: string;
    status?: string;
    search?: string;
    limit?: number;
    lastKey?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
  }): Promise<PaginatedResponse<Cell>> => {
    const response = await api.get<PaginatedResponse<Cell>>('/cells', {
      params,
    });
    return response.data;
  },

  get: async (id: string): Promise<Cell> => {
    const response = await api.get<Cell>(`/cells/${id}`);
    return response.data;
  },

  create: async (data: CreateCellInput): Promise<Cell> => {
    const response = await api.post<Cell>('/cells', data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/cells/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<CreateCellInput>): Promise<Cell> => {
    const response = await api.patch<Cell>(`/cells/${id}`, data);
    return response.data;
  },
};

export const testsApi = {
  list: async (packId: string, params?: {
    limit?: number;
    lastKey?: string;
  }): Promise<PaginatedResponse<Test>> => {
    const response = await api.get<PaginatedResponse<Test>>('/tests', {
      params: { pack_id: packId, ...params },
    });
    return response.data;
  },

  history: async (params?: {
    search?: string;
    limit?: number;
    lastKey?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
  }): Promise<PaginatedResponse<Test>> => {
    const response = await api.get<PaginatedResponse<Test>>('/tests/history', {
      params,
    });
    return response.data;
  },

  create: async (data: CreateTestInput): Promise<Test> => {
    const response = await api.post<Test>('/tests', data);
    return response.data;
  },

  get: async (packId: string, testId: string): Promise<Test> => {
    const response = await api.get<Test>(`/tests/${packId}/${testId}`);
    return response.data;
  },

  update: async (packId: string, testId: string, data: Partial<CreateTestInput>): Promise<Test> => {
    const response = await api.patch<Test>(`/tests/${packId}/${testId}`, data);
    return response.data;
  },
};

export const importApi = {
  upload: async (file: File, type: 'cells' | 'packs' | 'tests'): Promise<{
    total: number;
    batches: number;
    duplicatesRemoved?: number;
    successCount: number;
    failedCount: number;
    failedItems?: Array<{ key: string; error: string }>;
    results: Array<{ success: boolean; count: number; error?: string }>;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await api.post('/import', formData);
    return response.data;
  },
};

export interface Stats {
  totalPacks: number;
  totalCells: number;
  totalCustomers: number;
  totalTests: number;
  energy: {
    available: number;
    consumed: number;
    scrapped: number;
  };
}

export const statsApi = {
  get: async (): Promise<Stats> => {
    const { data } = await api.get<Stats>('/stats');
    return data;
  },
};

export interface Customer {
  customer: string;
  customer_location?: string;
  pack_count: number;
  packs: Array<{ pack_id: string; batch_id?: string }>;
}

export interface CreateCustomerInput {
  customer: string;
  customer_location?: string;
}

export const customersApi = {
  list: async (params?: {
    limit?: number;
    lastKey?: string;
  }): Promise<PaginatedResponse<Customer>> => {
    const response = await api.get<PaginatedResponse<Customer>>('/customers', {
      params,
    });
    return response.data;
  },

  create: async (data: CreateCustomerInput): Promise<{ customer: string; customer_location?: string }> => {
    const response = await api.post<{ customer: string; customer_location?: string }>('/customers', data);
    return response.data;
  },

  delete: async (customer: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/customers/${encodeURIComponent(customer)}`);
    return response.data;
  },

  update: async (customer: string, data: { customer_location: string }): Promise<{ customer: string; customer_location: string }> => {
    const response = await api.patch<{ customer: string; customer_location: string }>(`/customers/${encodeURIComponent(customer)}`, data);
    return response.data;
  },
};

export const exportApi = {
  download: async (type: 'cells' | 'packs' | 'tests'): Promise<void> => {
    const response = await api.get(`/export/${type}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export const searchApi = {
  search: async (query: string): Promise<{
    packs: Array<{ pack_id: string; batch_id: string; customer: string; type: string }>;
    cells: Array<{ cell_id: string; type: string }>;
    customers: Array<{ customer: string; pack_count: number; type: string }>;
  }> => {
    const response = await api.get('/search', {
      params: { q: query },
    });
    return response.data;
  },
};

export const auditApi = {
  list: async (params?: {
    entityType?: 'PACK' | 'CELL' | 'CUSTOMER' | 'TEST';
    entityId?: string;
    action?: 'CREATE' | 'UPDATE' | 'DELETE' | 'SOFT_DELETE';
    limit?: number;
    lastKey?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
  }): Promise<PaginatedResponse<AuditLog>> => {
    const response = await api.get<PaginatedResponse<AuditLog>>('/audit', {
      params,
    });
    return response.data;
  },

  getDetail: async (pk: string, sk: string): Promise<AuditLog> => {
    const encodedPK = utf8ToBase64UrlPath(pk);
    const encodedSK = utf8ToBase64UrlPath(sk);
    const response = await api.get<AuditLog>(`/audit/${encodedPK}/${encodedSK}`);
    return response.data;
  },
};

export default api;


